/**
 * Budget × School intersection — the first true constraint-intersection layer.
 *
 * Why this exists:
 *   Priority + school fact alone ("Balwyn HS zone premium") doesn't answer the
 *   question families actually ask: "given my budget, is this pocket a Good
 *   fit / Stretch / Weak fit?" This module provides that classification using
 *   only the existing `priceRange` + `tags` + school-fact catalog — no new
 *   data, no external APIs, no school ranking tables.
 *
 * Design rules:
 *   - Pure functions. No DOM, no storage reads, no side effects.
 *   - `classifyFit` maps (verdict, budget band) → "good" | "stretch" | "weak".
 *   - Copy helpers return short strings consumed by constraint-layer for
 *     Best-for / Tradeoff, and by the cards for the decision anchor.
 *   - The classifier never returns a numeric score. This axis is categorical
 *     on purpose. (Phase-2 score-removal note: compare-card no longer has
 *     any numeric scoring of its own — the entire `scoreAll` / per-facet 0–10
 *     vector was deleted, so this file's "categorical-on-purpose" stance is
 *     now the house style across the card surface, not a local choice.)
 */

import type { Verdict } from "../data/verdicts";
import type { BudgetBand } from "./budget-band";
import { getSchoolFact } from "./school-facts";

export type Fit = "good" | "stretch" | "weak";

/** Suburb price tier (0=cheap / 1=mid / 2=premium). */
type Tier = 0 | 1 | 2;

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}

/**
 * Pull the midpoint number out of the priceRange string. Works for both
 * HYD ("₹8k–13k / sqft") and MEL ("A$11k–16k / m²") because we grab raw
 * digits and average — the unit label doesn't affect the midpoint.
 */
function priceMidK(range: string): number | null {
  const nums = [...range.matchAll(/(\d+(?:\.\d+)?)/g)].map((m) => parseFloat(m[1]));
  if (nums.length === 0) return null;
  return (nums[0] + nums[nums.length - 1]) / 2;
}

/**
 * Classify the suburb into a price tier using tags first (authoritative),
 * midpoint as a fallback. Tag wins so any dataset change that retags a pocket
 * as "premium-inner" / "growth-corridor" reclassifies it without editing
 * thresholds. Thresholds match the ones already used by `budgetBestFor` so
 * Budget-only and Budget+School output read the same "scale" of suburb.
 */
function suburbTier(v: Verdict): Tier {
  const t = tagSet(v);
  // Premium — catchment-premium candidates almost always sit here.
  if (t.has("premium-inner") || t.has("premium") || t.has("heritage")) return 2;
  // Cheap — growth-corridor and new-estate belts.
  if (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) return 0;

  const mid = priceMidK(v.priceRange);
  if (mid != null) {
    if (mid >= 18) return 2;
    if (mid <= 9) return 0;
    return 1;
  }
  return 1;
}

/** User wallet → budget tier (0=tight / 1=moderate / 2=stretch). */
function bandTier(b: BudgetBand): Tier | null {
  if (b === "tight") return 0;
  if (b === "moderate") return 1;
  if (b === "stretch") return 2;
  return null;
}

/**
 * Classify (suburb, band) → Good / Stretch / Weak.
 *
 * Rules (in order):
 *   1. `limited` school tone → Weak. Under School priority, a pocket where
 *      school isn't the pull is never a good fit regardless of wallet.
 *   2. Suburb tier is within budget tier → Good. School tone (strong / mixed
 *      / emerging) all count as "usable school story" when wallet can reach.
 *   3. Suburb is exactly one tier above budget → Stretch. This is the
 *      catchment-premium case — the zone's draw is real but your budget has
 *      to reach further than comfortable.
 *   4. Two+ tiers above budget → Weak. Even with a stretch, the wallet gap
 *      is too wide to call this a viable intersection.
 *
 * Returns "good" by convention when band === "none" callers shouldn't have
 * invoked — but the public API is `classifyFit`, which guards that.
 */
function classify(tone: "strong" | "mixed" | "limited" | "emerging", gap: number): Fit {
  if (tone === "limited") return "weak";
  if (gap <= 0) return "good";
  if (gap === 1) return "stretch";
  return "weak";
}

export function classifyFit(v: Verdict, band: BudgetBand): Fit | null {
  const userTier = bandTier(band);
  if (userTier === null) return null;
  const fact = getSchoolFact(v);
  const gap = suburbTier(v) - userTier;
  return classify(fact.tone, gap);
}

/* -------------------------------------------------------------------------- */
/*  Copy helpers                                                              */
/* -------------------------------------------------------------------------- */

function bandLabel(b: BudgetBand): string {
  if (b === "tight") return "tight";
  if (b === "moderate") return "mid-range";
  if (b === "stretch") return "stretch";
  return "unspecified";
}

/**
 * Best-for copy under School priority + budget band. Drops the bare
 * "Good fit / Stretch / Weak fit" verdict word and writes the audience
 * directly — so the user reads who the suburb works for, not how the
 * classifier scored it. Keeps the school hook grounded in a fact bullet.
 */
export function fitBestFor(v: Verdict, band: BudgetBand, fit: Fit): string {
  const hook = getSchoolFact(v).bullet;
  const b = bandLabel(band);
  if (fit === "good") {
    return `Works for families prioritising schools on a ${b} budget — ${hook}`;
  }
  if (fit === "stretch") {
    return `Works for families set on this school zone — prices already reflect school demand (${hook})`;
  }
  return `Avoid if public school access is the main goal — ${hook}`;
}

/**
 * Tradeoff copy under School priority + budget band. Pairs the audience
 * framing with the concrete consequence so the user knows what they're
 * giving up.
 */
export function fitTradeoff(v: Verdict, band: BudgetBand, fit: Fit): string {
  const signal = getSchoolFact(v).signal;
  const b = bandLabel(band);
  if (fit === "good") {
    // Within budget AND school signal — use the concrete fact verbatim so the
    // copy reads grounded not handwaved.
    return signal;
  }
  if (fit === "stretch") {
    return `prices already reflect school demand — expect to stretch beyond a ${b} budget`;
  }
  // weak
  return `${signal} — at a ${b} budget, public school access shouldn't be the deciding reason`;
}

/**
 * Decision anchor — the closing "If you had to choose today" phrase when
 * School priority + budget band are both active. Replaces the lens-only
 * anchor so the user's non-negotiable pair drives the close.
 */
export function fitDecisionAnchor(fit: Fit): string {
  if (fit === "good") return "schools + your budget line up — act with confidence";
  if (fit === "stretch") return "the school-zone premium is real — walk your budget before committing";
  return "school access is weak at this budget — keep looking";
}

/**
 * Short audience word for chips / badges. The classification still drives
 * styling (colour token, ordering), but the visible word is audience-led
 * — no bare "Good fit / Stretch / Weak fit" labels reach the surface.
 */
export function fitLabel(fit: Fit): string {
  if (fit === "good") return "Schools work";
  if (fit === "stretch") return "Schools — at a stretch";
  return "Schools weak";
}
